import { PlayerRef, Player as RemotionPlayer, RenderPoster } from '@remotion/player';
import { useCallback, useMemo, memo } from 'react';
import { AbsoluteFill } from 'remotion';
import { CompositionConfig } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import MainPlayer from '../MainPlayer';
import { NUMBER_OF_SHARED_AUDIO_TAGS } from '../constants';
import { RemotionUsage, RemotionUsageContextProvider } from '../RemotionUsageContext/RemotionUsageContext';
import BufferingPoster from './BufferingPoster';
import { remotionPlayerStateStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import { useNewVideoTagInPreview } from '@/hooks/useNewVideoTagInPreview';
import { usePreviewVolumeStore } from '@/stores/volumePreviewStore';
import { isPlayRangeMicroCutsEnabled, isUploadsPreviewAudioDiscontinuityEnabled } from '@/stores/clip';

type RemotionPlayerOnlyProps = Pick<
  CompositionConfig,
  | 'onOutlineRelease'
  | 'onOutlineResize'
  | 'canEditCaptions'
  | 'canEditSpeakerLabel'
  | 'disableCaptionsDragging'
  | 'disableCaptionsResize'
  | 'showPlayerFrames'
  | 'canEditImages'
  | 'canEditTextElements'
  | 'canEditVideos'
  | 'canManuallyReposition'
  | 'canEditLottie'
  | 'preventOverlappingWithOtherCrops'
> & {
  compositionHeight: number;
  compositionWidth: number;
  playbackRate: number;
  durationInFrames: number;
  isSharedPage?: boolean;
  playerConfig: CompositionConfig;
  remotionPlayerRef: React.RefObject<PlayerRef>;
  fps: number;
  clipId: string;
};

function RemotionPlayerOnly({
  playerConfig,
  compositionHeight,
  compositionWidth,
  playbackRate,
  durationInFrames,
  showPlayerFrames,
  canEditCaptions = false,
  disableCaptionsDragging = true,
  disableCaptionsResize = true,
  canEditSpeakerLabel = false,
  canEditImages = false,
  canManuallyReposition = false,
  canEditTextElements = false,
  canEditVideos = false,
  canEditLottie = false,
  isSharedPage = false,
  remotionPlayerRef,
  clipId,
  onOutlineRelease,
  onOutlineResize,
  fps,
  preventOverlappingWithOtherCrops = false
}: RemotionPlayerOnlyProps) {
  const mostlyStaticInputProps = useMemo(
    () => ({
      showPlayerFrames,
      onOutlineRelease,
      onOutlineResize,
      canEditCaptions,
      disableCaptionsDragging,
      disableCaptionsResize,
      canEditSpeakerLabel,
      canEditImages,
      canManuallyReposition,
      preventOverlappingWithOtherCrops,
      canEditTextElements,
      canEditVideos,
      canEditLottie
    }),
    [
      showPlayerFrames,
      onOutlineRelease,
      onOutlineResize,
      canEditCaptions,
      disableCaptionsDragging,
      disableCaptionsResize,
      canEditSpeakerLabel,
      canEditImages,
      canManuallyReposition,
      canEditTextElements,
      preventOverlappingWithOtherCrops,
      canEditVideos,
      canEditLottie
    ]
  );

  const [inputProps, initialFrame]: [CompositionConfig, number | undefined] = useMemo(() => {
    const currentTime = Math.floor(remotionPlayerStateStore.getSnapshot().currentTime * fps);
    const maxDuration = playerConfig.durationInFrames ? playerConfig.durationInFrames - 1 : 0;
    const initialFrame = maxDuration > currentTime ? currentTime : undefined;
    return [
      {
        ...playerConfig,
        ...mostlyStaticInputProps
      },
      initialFrame
    ];
  }, [playerConfig, mostlyStaticInputProps, fps]);

  const renderPoster: RenderPoster = useCallback(({ height, width, isBuffering }) => {
    if (isBuffering) {
      return <BufferingPoster clipId={clipId} />;
    }

    return (
      <AbsoluteFill style={{ backgroundColor: 'gray' }}>
        Click to play! ({height}x{width})
      </AbsoluteFill>
    );
  }, []);

  const style = useMemo(
    () => ({
      width: '100%'
    }),
    []
  );

  const { useNewVideoTag } = useNewVideoTagInPreview();

  const previewVolume = usePreviewVolumeStore();

  return (
    <RemotionUsageContextProvider
      useNewVideoTag={useNewVideoTag}
      usage={RemotionUsage.PLAYER}
      isSharedPage={isSharedPage}
      isPlayRangeMicroCutsEnabled={isPlayRangeMicroCutsEnabled()}
      isUploadsPreviewAudioDiscontinuityEnabled={isUploadsPreviewAudioDiscontinuityEnabled()}
      previewVolume={previewVolume}
    >
      <RemotionPlayer
        component={MainPlayer}
        durationInFrames={Math.floor(durationInFrames)}
        compositionWidth={compositionWidth}
        compositionHeight={compositionHeight}
        fps={fps}
        ref={remotionPlayerRef}
        clickToPlay={false}
        className="player-container"
        spaceKeyToPlayOrPause={false}
        controls={showPlayerFrames}
        playbackRate={playbackRate}
        initialFrame={initialFrame}
        inputProps={inputProps}
        style={style}
        showVolumeControls
        numberOfSharedAudioTags={NUMBER_OF_SHARED_AUDIO_TAGS}
        renderPoster={renderPoster}
        showPosterWhenBuffering
        showPosterWhenPaused={false}
      />
    </RemotionUsageContextProvider>
  );
}

export default memo(RemotionPlayerOnly);
